import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Spinner, Alert } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { materialsAPI } from '../utils/api';

const formatCurrency = (v) =>
  new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(v ?? 0);

const formatDate = (dateStr) =>
  new Date(dateStr).toLocaleString('th-TH', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

const formatDateShort = (dateStr) =>
  new Date(dateStr).toLocaleDateString('th-TH', { month: 'short', day: 'numeric', year: '2-digit' });

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded shadow-sm p-2" style={{ fontSize: '0.85rem' }}>
      <p className="mb-1 fw-bold">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} style={{ color: p.color }} className="mb-0">
          {p.name}: {formatCurrency(p.value)}
        </p>
      ))}
    </div>
  );
};

const MaterialPriceHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [material, setMaterial] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await materialsAPI.getPriceHistory(id);
        setMaterial(res.data.data.material);
        setHistory(res.data.data.history);
      } catch (err) {
        setError(err.response?.data?.message || 'เกิดข้อผิดพลาดในการดึงข้อมูลประวัติราคา');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // Build chart data — one point per event with running salePrice and cost values
  const chartData = useMemo(() => {
    if (!history.length) return [];

    // Find the initial "before" values from the first event of each field
    const firstSP = history.find(h => h.field === 'salePrice');
    const firstCost = history.find(h => h.field === 'cost');

    let curSalePrice = firstSP?.oldPrice ?? null;
    let curCost = firstCost?.oldPrice ?? null;

    const points = [];

    // Add a "start" anchor point before first change
    if (curSalePrice !== null || curCost !== null) {
      points.push({
        date: formatDateShort(history[0].createdAt),
        fullDate: history[0].createdAt,
        salePrice: curSalePrice,
        cost: curCost
      });
    }

    for (const entry of history) {
      if (entry.field === 'salePrice') curSalePrice = entry.newPrice;
      else curCost = entry.newPrice;

      points.push({
        date: formatDateShort(entry.createdAt),
        fullDate: entry.createdAt,
        salePrice: curSalePrice,
        cost: curCost
      });
    }

    return points;
  }, [history]);

  if (loading) {
    return (
      <Container fluid className="main-content">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="main-content">
        <Alert variant="danger">{error}</Alert>
        <Button variant="outline-secondary" onClick={() => navigate('/materials')}>
          <i className="bi bi-arrow-left me-2"></i>กลับ
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="main-content">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <Button
            variant="outline-secondary"
            size="sm"
            className="mb-2"
            onClick={() => navigate('/materials')}
          >
            <i className="bi bi-arrow-left me-1"></i>
            กลับรายการวัตถุดิบ
          </Button>
          <h2>
            <i className="bi bi-graph-up me-2"></i>
            ประวัติราคา
          </h2>
          <p className="text-muted mb-0">
            <strong>{material?.code}</strong> — {material?.name}
          </p>
        </Col>
        <Col xs="auto" className="d-flex align-items-end gap-3">
          {history.length > 0 && (
            <>
              <div className="text-end">
                <div className="text-muted small">ราคาสาขาปัจจุบัน</div>
                <strong className="text-primary fs-5">
                  {formatCurrency(chartData[chartData.length - 1]?.salePrice)}
                </strong>
              </div>
              <div className="text-end">
                <div className="text-muted small">ต้นทุนปัจจุบัน</div>
                <strong className="text-warning fs-5">
                  {formatCurrency(chartData[chartData.length - 1]?.cost)}
                </strong>
              </div>
            </>
          )}
        </Col>
      </Row>

      {/* Chart */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <i className="bi bi-bar-chart-line me-2"></i>
              กราฟความเคลื่อนไหวราคา
              <span className="text-muted small ms-2">({history.length} รายการเปลี่ยนแปลง)</span>
            </Card.Header>
            <Card.Body>
              {chartData.length < 2 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-clock-history" style={{ fontSize: '2.5rem' }}></i>
                  <p className="mt-3">ยังไม่มีข้อมูลเพียงพอสำหรับแสดงกราฟ</p>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      tickFormatter={(v) => `฿${v?.toLocaleString('th-TH')}`}
                      tick={{ fontSize: 12 }}
                      width={80}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="stepAfter"
                      dataKey="salePrice"
                      name="ราคาสาขา"
                      stroke="#0d6efd"
                      strokeWidth={2}
                      dot={{ r: 4, fill: '#0d6efd' }}
                      activeDot={{ r: 6 }}
                      connectNulls
                    />
                    <Line
                      type="stepAfter"
                      dataKey="cost"
                      name="ต้นทุน"
                      stroke="#ffc107"
                      strokeWidth={2}
                      dot={{ r: 4, fill: '#ffc107' }}
                      activeDot={{ r: 6 }}
                      connectNulls
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* History table */}
      <Row>
        <Col>
          <Card>
            <Card.Header>
              <i className="bi bi-list-ul me-2"></i>
              รายละเอียดการเปลี่ยนแปลง
            </Card.Header>
            <Card.Body className="p-0">
              {history.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="bi bi-clock-history" style={{ fontSize: '2.5rem' }}></i>
                  <p className="mt-3">ยังไม่มีประวัติการเปลี่ยนแปลงราคา</p>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>วันที่/เวลา</th>
                      <th>ประเภทราคา</th>
                      <th className="text-end">ราคาเก่า</th>
                      <th className="text-end">ราคาใหม่</th>
                      <th className="text-end">เปลี่ยนแปลง</th>
                      <th>แก้ไขโดย</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...history].reverse().map(entry => {
                      const diff = entry.newPrice - entry.oldPrice;
                      return (
                        <tr key={entry.id}>
                          <td className="text-muted small">{formatDate(entry.createdAt)}</td>
                          <td>
                            {entry.field === 'salePrice'
                              ? <Badge bg="primary">ราคาสาขา</Badge>
                              : <Badge bg="warning" text="dark">ต้นทุน</Badge>}
                          </td>
                          <td className="text-end">{formatCurrency(entry.oldPrice)}</td>
                          <td className="text-end fw-bold">{formatCurrency(entry.newPrice)}</td>
                          <td className={`text-end fw-bold ${diff > 0 ? 'text-danger' : diff < 0 ? 'text-success' : 'text-muted'}`}>
                            {diff > 0 ? '+' : ''}{formatCurrency(diff)}
                          </td>
                          <td className="text-muted small">
                            {entry.changedBy?.fullName || entry.changedBy?.username || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default MaterialPriceHistory;
